import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Checkbox, ContentLoader, InputErrorMessage } from 'shared/components';
import {
  formatManagerPermissions,
  getCurrentUserPermissions,
} from 'shared/helpers';
import { fetchDefaultManagerPermissions } from 'orders/OrderContainer/actions';
import './styles.scss';

// permissions is null while the company defaults apply, otherwise the chosen codes.
// onLoadingChange lets the form block saving while the defaults are loading
const ManagerPermissionsPicker = ({
  permissions,
  onChange,
  onLoadingChange,
  error,
  disabled,
}) => {
  const { t } = useTranslation();
  const userID = useSelector((state) => get(state, 'user.details.id'));
  const ownPermissions = useSelector((state) =>
    formatManagerPermissions(getCurrentUserPermissions(state)),
  );
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [defaultsError, setDefaultsError] = useState(false);
  const isMounted = useRef(true);
  const isLoading = useRef(false);
  const loadingChangeCb = useRef(onLoadingChange);
  const isChoosing = Array.isArray(permissions);

  // The form can hide the picker while the defaults are loading, and must not
  // stay blocked when it does
  useEffect(
    () => () => {
      isMounted.current = false;
      if (isLoading.current) {
        loadingChangeCb.current(false);
      }
    },
    [],
  );

  const setLoading = (value) => {
    isLoading.current = value;
    setLoadingDefaults(value);
    onLoadingChange(value);
  };

  // Only the manager's own permissions can be given, so the company defaults
  // are preselected only where the manager holds them
  const handleToggle = (checked) => {
    if (!checked) {
      onChange(null);
      return;
    }
    onChange([]);
    setDefaultsError(false);
    setLoading(true);
    // The form may be closed before the defaults arrive
    fetchDefaultManagerPermissions(userID)
      .then((res) => {
        if (!isMounted.current) {
          return;
        }
        const defaultCodes = get(res, 'data', []).map((p) => p.code);
        onChange(
          ownPermissions
            .map((p) => p.code)
            .filter((code) => defaultCodes.includes(code)),
        );
      })
      .catch(() => isMounted.current && setDefaultsError(true))
      .finally(() => isMounted.current && setLoading(false));
  };

  const togglePermission = (code, checked) =>
    onChange(
      checked
        ? permissions.concat(code)
        : permissions.filter((permission) => permission !== code),
    );

  const renderPermissions = () => {
    if (loadingDefaults) {
      return <ContentLoader />;
    }
    return (
      <div className="ManagerPermissionsPicker-list">
        {defaultsError && (
          <div className="ManagerPermissionsPicker-note ManagerPermissionsPicker-note--error">
            {t(
              'The default permissions could not be loaded, so none are preselected.',
            )}
          </div>
        )}
        {ownPermissions.map((permission) => (
          <div key={permission.code} className="ManagerPermissionsPicker-item">
            <Checkbox
              inputId={`manager-permission-${permission.code}`}
              label={permission.label}
              value={permissions.includes(permission.code)}
              disabled={disabled}
              handleChange={(checked) =>
                togglePermission(permission.code, checked)
              }
            />
            {permission.desc && (
              <div className="ManagerPermissionsPicker-desc">
                {permission.desc}
              </div>
            )}
          </div>
        ))}
        <InputErrorMessage text={error} />
      </div>
    );
  };

  return (
    <div className="ManagerPermissionsPicker">
      <div className="ManagerPermissionsPicker-note">
        {isChoosing
          ? t(
              'New license managers get the permissions selected below. You can only give permissions you have.',
            )
          : t(
              'New license managers get the default license manager permissions.',
            )}
        &nbsp;
        {t(
          'People who are already license managers keep their current permissions.',
        )}
      </div>
      <Checkbox
        inputId="choose-manager-permissions"
        label={t('Choose permissions')}
        value={isChoosing}
        disabled={disabled || loadingDefaults}
        handleChange={handleToggle}
      />
      {isChoosing && renderPermissions()}
    </div>
  );
};

ManagerPermissionsPicker.propTypes = {
  permissions: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  onLoadingChange: PropTypes.func,
  error: PropTypes.string,
  disabled: PropTypes.bool,
};

ManagerPermissionsPicker.defaultProps = {
  permissions: null,
  onLoadingChange: () => {},
  error: '',
  disabled: false,
};

export default ManagerPermissionsPicker;
